import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2, Upload, Save, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/data/content";

const clone = (o) => JSON.parse(JSON.stringify(o));

function mergeContent(stored) {
  const out = clone(DEFAULT_CONTENT);
  if (stored && typeof stored === "object") {
    for (const key of Object.keys(DEFAULT_CONTENT)) {
      const v = stored[key];
      if (Array.isArray(v) && v.length) out[key] = v;
      else if (v && !Array.isArray(v) && typeof v === "object") out[key] = { ...out[key], ...v };
    }
  }
  return out;
}

const inputCls =
  "rounded-none border-[#2c332f] bg-[#0d110f] text-[#f2efeb] placeholder:text-[#6b716d] focus-visible:ring-[#ba5a45] focus-visible:ring-1 h-10";

export default function AdminContent() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("aruha_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/content")
      .then((res) => setContent(mergeContent(res.data)))
      .catch(() => setContent(clone(DEFAULT_CONTENT)))
      .finally(() => setLoading(false));
  }, [navigate]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Content saved", { description: "Your changes are live immediately." });
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  // helpers to update nested content
  const setArrItem = (key, idx, field, value) =>
    setContent((c) => {
      const next = clone(c);
      next[key][idx][field] = value;
      return next;
    });
  const addArrItem = (key, template) =>
    setContent((c) => {
      const next = clone(c);
      next[key] = [...next[key], template];
      return next;
    });
  const removeArrItem = (key, idx) =>
    setContent((c) => {
      const next = clone(c);
      next[key].splice(idx, 1);
      return next;
    });
  const setObj = (key, field, value) =>
    setContent((c) => ({ ...c, [key]: { ...c[key], [field]: value } }));

  if (loading || !content) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0d110f]">
        <Loader2 className="h-6 w-6 animate-spin text-[#ba5a45]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d110f] text-[#f2efeb]">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#242a27] bg-[#0d110f]/95 px-6 py-4 backdrop-blur lg:px-10">
        <button
          data-testid="content-back-btn"
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-sm text-[#a1a8a4] hover:text-[#f2efeb]"
        >
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </button>
        <div className="font-serif text-lg">Content Manager</div>
        <Button
          data-testid="content-save-btn"
          onClick={save}
          disabled={saving}
          className="rounded-none bg-[#ba5a45] text-xs font-semibold uppercase tracking-wider text-[#f2efeb] hover:bg-[#9c4633]"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save</>}
        </Button>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-8 lg:px-10">
        <p className="mb-6 text-sm text-[#a1a8a4]">
          Edit any section below, upload photos, then <strong className="text-[#f2efeb]">Save</strong>.
          On your live site (aruhatrails.com/admin) changes go live instantly — no redeploy needed.
        </p>

        <Accordion type="multiple" defaultValue={["hero"]} className="space-y-3">
          {/* HERO SLIDES */}
          <Section value="hero" title="Hero Slideshow" count={content.hero_slides.length}>
            {content.hero_slides.map((s, i) => (
              <Card key={i} onRemove={() => removeArrItem("hero_slides", i)} testid={`hero-${i}`}>
                <ImageField
                  label="Slide image"
                  value={s.image}
                  onChange={(url) => setArrItem("hero_slides", i, "image", url)}
                  testid={`hero-img-${i}`}
                />
                <Row>
                  <Field label="Line 1">
                    <Input className={inputCls} value={s.line1 || ""} onChange={(e) => setArrItem("hero_slides", i, "line1", e.target.value)} />
                  </Field>
                  <Field label="Line 2 (italic)">
                    <Input className={inputCls} value={s.line2 || ""} onChange={(e) => setArrItem("hero_slides", i, "line2", e.target.value)} />
                  </Field>
                </Row>
                <Field label="Caption (small overline)">
                  <Input className={inputCls} value={s.caption || ""} onChange={(e) => setArrItem("hero_slides", i, "caption", e.target.value)} />
                </Field>
              </Card>
            ))}
            <AddBtn onClick={() => addArrItem("hero_slides", { image: "", focal: "center 40%", line1: "New line", line2: "here", caption: "Caption" })} label="Add slide" />
          </Section>

          {/* ADVENTURES */}
          <Section value="adventures" title="Experiences & Tours" count={content.adventures.length}>
            {content.adventures.map((a, i) => (
              <Card key={i} onRemove={() => removeArrItem("adventures", i)} testid={`adv-${i}`}>
                <ImageField label="Photo" value={a.image} onChange={(url) => setArrItem("adventures", i, "image", url)} testid={`adv-img-${i}`} />
                <Row>
                  <Field label="Name">
                    <Input className={inputCls} value={a.name || ""} onChange={(e) => setArrItem("adventures", i, "name", e.target.value)} />
                  </Field>
                  <Field label="Category">
                    <Input className={inputCls} value={a.category || ""} onChange={(e) => setArrItem("adventures", i, "category", e.target.value)} />
                  </Field>
                </Row>
                <Field label="Short description">
                  <Textarea className={`${inputCls} h-20`} value={a.blurb || ""} onChange={(e) => setArrItem("adventures", i, "blurb", e.target.value)} />
                </Field>
              </Card>
            ))}
            <AddBtn onClick={() => addArrItem("adventures", { id: `adv-${Date.now()}`, name: "New experience", category: "Adventure", image: "", blurb: "Describe it", span: "lg:col-span-4" })} label="Add adventure" />
          </Section>

          {/* STORY CHAPTERS */}
          <Section value="story" title="Our Story (chapters)" count={content.chapters.length}>
            {content.chapters.map((c, i) => (
              <Card key={i} onRemove={() => removeArrItem("chapters", i)} testid={`chap-${i}`}>
                <Row>
                  <Field label="Number">
                    <Input className={inputCls} value={c.no || ""} onChange={(e) => setArrItem("chapters", i, "no", e.target.value)} />
                  </Field>
                  <Field label="Title">
                    <Input className={inputCls} value={c.title || ""} onChange={(e) => setArrItem("chapters", i, "title", e.target.value)} />
                  </Field>
                </Row>
                <Field label="Body">
                  <Textarea className={`${inputCls} h-20`} value={c.body || ""} onChange={(e) => setArrItem("chapters", i, "body", e.target.value)} />
                </Field>
              </Card>
            ))}
            <AddBtn onClick={() => addArrItem("chapters", { no: "04", title: "New chapter", body: "Body text" })} label="Add chapter" />
          </Section>

          {/* CAFE MENU */}
          <Section value="menu" title="Cafe Menu" count={content.menu.length}>
            {content.menu.map((m, i) => (
              <Card key={i} onRemove={() => removeArrItem("menu", i)} testid={`menu-${i}`}>
                <Row>
                  <Field label="Category">
                    <Input className={inputCls} value={m.tag || ""} onChange={(e) => setArrItem("menu", i, "tag", e.target.value)} />
                  </Field>
                  <Field label="Price">
                    <Input className={inputCls} value={m.price || ""} onChange={(e) => setArrItem("menu", i, "price", e.target.value)} />
                  </Field>
                </Row>
                <Field label="Dish name">
                  <Input className={inputCls} value={m.name || ""} onChange={(e) => setArrItem("menu", i, "name", e.target.value)} />
                </Field>
                <Field label="Description">
                  <Input className={inputCls} value={m.desc || ""} onChange={(e) => setArrItem("menu", i, "desc", e.target.value)} />
                </Field>
              </Card>
            ))}
            <AddBtn onClick={() => addArrItem("menu", { tag: "Mains", name: "New dish", desc: "Description", price: "₹0" })} label="Add menu item" />
          </Section>

          {/* SEASONS */}
          <Section value="seasons" title="Best Time to Visit (seasons)" count={content.seasons.length}>
            {content.seasons.map((s, i) => (
              <Card key={i} onRemove={() => removeArrItem("seasons", i)} testid={`season-${i}`}>
                <ImageField label="Photo" value={s.image} onChange={(url) => setArrItem("seasons", i, "image", url)} testid={`season-img-${i}`} />
                <Row>
                  <Field label="Season">
                    <Input className={inputCls} value={s.season || ""} onChange={(e) => setArrItem("seasons", i, "season", e.target.value)} />
                  </Field>
                  <Field label="Months">
                    <Input className={inputCls} value={s.months || ""} onChange={(e) => setArrItem("seasons", i, "months", e.target.value)} />
                  </Field>
                </Row>
                <Field label="Tag/Title">
                  <Input className={inputCls} value={s.title || ""} onChange={(e) => setArrItem("seasons", i, "title", e.target.value)} />
                </Field>
                <Field label="Description">
                  <Textarea className={`${inputCls} h-20`} value={s.body || ""} onChange={(e) => setArrItem("seasons", i, "body", e.target.value)} />
                </Field>
              </Card>
            ))}
            <AddBtn onClick={() => addArrItem("seasons", { season: "Season", months: "Months", title: "Title", body: "Body", image: "" })} label="Add season" />
          </Section>

          {/* GALLERY */}
          <Section value="gallery" title="Photo Gallery" count={content.gallery.length}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {content.gallery.map((g, i) => (
                <Card key={i} onRemove={() => removeArrItem("gallery", i)} testid={`gal-${i}`}>
                  <ImageField label="Photo" value={g.src} onChange={(url) => setArrItem("gallery", i, "src", url)} testid={`gal-img-${i}`} />
                  <Field label="Caption">
                    <Input className={inputCls} value={g.caption || ""} onChange={(e) => setArrItem("gallery", i, "caption", e.target.value)} />
                  </Field>
                </Card>
              ))}
            </div>
            <AddBtn onClick={() => addArrItem("gallery", { src: "", caption: "New photo", focal: "center", cls: "lg:col-span-4 aspect-square" })} label="Add photo" />
          </Section>

          {/* EVENTS */}
          <Section value="events" title="Mountain Music Events" count={(content.events || []).length}>
            {(content.events || []).map((e, i) => (
              <Card key={i} onRemove={() => removeArrItem("events", i)} testid={`event-${i}`}>
                <ImageField label="Photo" value={e.image} onChange={(url) => setArrItem("events", i, "image", url)} testid={`event-img-${i}`} />
                <Row>
                  <Field label="Season">
                    <Input className={inputCls} value={e.season || ""} onChange={(ev) => setArrItem("events", i, "season", ev.target.value)} />
                  </Field>
                  <Field label="Months">
                    <Input className={inputCls} value={e.months || ""} onChange={(ev) => setArrItem("events", i, "months", ev.target.value)} />
                  </Field>
                </Row>
                <Field label="Event title">
                  <Input className={inputCls} value={e.title || ""} onChange={(ev) => setArrItem("events", i, "title", ev.target.value)} />
                </Field>
                <Field label="Description">
                  <Textarea className={`${inputCls} h-20`} value={e.body || ""} onChange={(ev) => setArrItem("events", i, "body", ev.target.value)} />
                </Field>
              </Card>
            ))}
            <AddBtn onClick={() => addArrItem("events", { season: "Season", months: "Months", title: "Event title", body: "Description", image: "" })} label="Add event" />
          </Section>

          {/* PRESS */}
          <Section value="press" title="'As featured in' names" count={content.press.length}>
            {content.press.map((p, i) => (
              <div key={i} className="mb-2 flex gap-2">
                <Input
                  className={inputCls}
                  value={p}
                  onChange={(e) =>
                    setContent((c) => {
                      const next = clone(c);
                      next.press[i] = e.target.value;
                      return next;
                    })
                  }
                />
                <Button onClick={() => removeArrItem("press", i)} variant="outline" className="rounded-none border-[#2c332f] bg-transparent text-[#d6a99f] hover:bg-[#4a2b26]">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <AddBtn onClick={() => addArrItem("press", "Publication name")} label="Add name" />
          </Section>

          {/* TESTIMONIAL */}
          <Section value="testimonial" title="Testimonial">
            <Field label="Quote">
              <Textarea className={`${inputCls} h-24`} value={content.testimonial.quote || ""} onChange={(e) => setObj("testimonial", "quote", e.target.value)} />
            </Field>
            <Field label="Author line">
              <Input className={inputCls} value={content.testimonial.author || ""} onChange={(e) => setObj("testimonial", "author", e.target.value)} />
            </Field>
          </Section>

          {/* CONTACT */}
          <Section value="contact" title="Contact & Location">
            <Row>
              <Field label="WhatsApp number (no +, e.g. 917668683973)">
                <Input className={inputCls} value={content.contact.whatsapp || ""} onChange={(e) => setObj("contact", "whatsapp", e.target.value)} />
              </Field>
              <Field label="Phone display (e.g. +91 76686 83973)">
                <Input className={inputCls} value={content.contact.phone_display || ""} onChange={(e) => setObj("contact", "phone_display", e.target.value)} />
              </Field>
            </Row>
            <Field label="Location (one line per row)">
              <Textarea className={`${inputCls} h-24`} value={content.contact.location || ""} onChange={(e) => setObj("contact", "location", e.target.value)} />
            </Field>
            <Field label="Hours (one line per row)">
              <Textarea className={`${inputCls} h-24`} value={content.contact.hours || ""} onChange={(e) => setObj("contact", "hours", e.target.value)} />
            </Field>
          </Section>
        </Accordion>

        <div className="mt-8 flex justify-end">
          <Button
            data-testid="content-save-btn-bottom"
            onClick={save}
            disabled={saving}
            className="rounded-none bg-[#ba5a45] px-8 text-xs font-semibold uppercase tracking-wider text-[#f2efeb] hover:bg-[#9c4633]"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save all changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------- small presentational helpers ---------- */
const Section = ({ value, title, count, children }) => (
  <AccordionItem value={value} className="border border-[#242a27] bg-[#151a18]">
    <AccordionTrigger data-testid={`section-${value}`} className="px-5 text-left font-serif text-xl hover:no-underline">
      {title}
      {count != null && <span className="ml-2 text-sm text-[#7a6c58]">({count})</span>}
    </AccordionTrigger>
    <AccordionContent className="px-5 pb-5">{children}</AccordionContent>
  </AccordionItem>
);

const Card = ({ children, onRemove, testid }) => (
  <div data-testid={testid} className="relative mb-4 space-y-3 border border-[#242a27] bg-[#0d110f] p-4">
    <button
      onClick={onRemove}
      className="absolute right-3 top-3 text-[#7a6c58] transition-colors hover:text-[#d6a99f]"
      title="Remove"
    >
      <Trash2 className="h-4 w-4" />
    </button>
    {children}
  </div>
);

const Row = ({ children }) => <div className="grid gap-3 sm:grid-cols-2">{children}</div>;

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.16em] text-[#a1a8a4]">{label}</Label>
    {children}
  </div>
);

const AddBtn = ({ onClick, label }) => (
  <Button onClick={onClick} variant="outline" className="rounded-none border-dashed border-[#3a423d] bg-transparent text-xs uppercase tracking-wider text-[#a1a8a4] hover:bg-[#0d110f] hover:text-[#f2efeb]">
    <Plus className="mr-2 h-4 w-4" /> {label}
  </Button>
);

const ImageField = ({ label, value, onChange, testid }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.16em] text-[#a1a8a4]">{label}</Label>
      <div className="flex items-center gap-3">
        <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden border border-[#242a27] bg-[#0d110f]">
          {value ? (
            <img src={value} alt="preview" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-5 w-5 text-[#3a423d]" />
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testid}-file`} />
        <Button
          type="button"
          data-testid={`${testid}-upload`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          variant="outline"
          className="rounded-none border-[#2c332f] bg-transparent text-xs uppercase tracking-wider text-[#d6d2cb] hover:bg-[#151a18] hover:text-[#f2efeb]"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-2 h-4 w-4" /> Upload</>}
        </Button>
      </div>
    </div>
  );
};
